
import React, { useState, useRef, useEffect, createContext, useContext } from 'react';
import { cn } from '../../utils/utils';
import { ChevronDown, Check } from 'lucide-react';

const SelectContext = createContext(null);

const useSelect = () => {
  const context = useContext(SelectContext);
  if (!context) {
    throw new Error("useSelect must be used within a Select provider");
  }
  return context;
};

const Select = ({ children, value, onValueChange, defaultValue }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || defaultValue || "");
  const selectRef = useRef(null);

  // Controlled vs Uncontrolled safe handling
  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  const handleSelect = (val) => {
    setSelectedValue(val);
    if (onValueChange) {
      onValueChange(val);
    }
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <SelectContext.Provider value={{ value: selectedValue, handleSelect, isOpen, setIsOpen }}>
      <div className="relative w-full" ref={selectRef}>
        {children}
      </div>
    </SelectContext.Provider>
  );
};

const SelectTrigger = ({ children, className }) => {
  const { isOpen, setIsOpen } = useSelect();
  return (
    <button
      type="button"
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 px-3 py-2 text-sm placeholder:text-zinc-500 dark:placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-left text-zinc-900 dark:text-white",
        className
      )}
      onClick={() => setIsOpen(!isOpen)}
      aria-haspopup="listbox"
      aria-expanded={isOpen}
    >
      {children}
      <ChevronDown className={cn("h-4 w-4 opacity-50 transition-transform", isOpen && "rotate-180")} />
    </button>
  );
};

const SelectValue = ({ placeholder, children }) => {
  const { value, labelMap } = useSelect();
  const displayValue = children || (value ? (labelMap[value] || value) : placeholder);

  return (
    <span className={cn(!value && "text-zinc-500 dark:text-zinc-400", "truncate")}>
      {displayValue}
    </span>
  );
};

// We need to hoist labels to context. 
// We will update Select to hold a map.
// But children render AFTER select.
// Simplified Approach: `SelectValue` renders a span. 
// `SelectItem` registers itself? 
// React render cycle makes this hard within one pass without re-render.
// ALTERNATIVE: Use a context state for labels.
// Let's modify Select Context.

const SelectContent = ({ children, className }) => {
  const { isOpen } = useSelect();
  if (!isOpen) return null;
  return (
    <div className={cn("absolute z-50 mt-2 w-full min-w-[8rem] overflow-hidden rounded-md border border-zinc-200 bg-white text-zinc-950 shadow-md animate-in fade-in-80 dark:border-zinc-800 dark:bg-zinc-950 dark:text-zinc-50", className)}>
      <div className="p-1" role="listbox">
        {children}
      </div>
    </div>
  );
};

const SelectItem = ({ children, value, className }) => {
  const { value: selectedValue, handleSelect, setLabelMap } = useSelect();
  const isSelected = selectedValue === value;

  // Register label on mount
  useEffect(() => {
    if (setLabelMap) {
      setLabelMap(prev => ({ ...prev, [value]: children }));
    }
  }, [value, children, setLabelMap]);

  return (
    <div
      className={cn(
        "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-zinc-100 dark:hover:bg-zinc-800 focus:bg-zinc-100 dark:focus:bg-zinc-800 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 text-zinc-900 dark:text-zinc-100",
        isSelected && "bg-zinc-100 dark:bg-zinc-800",
        className
      )}
      onClick={(e) => {
        e.stopPropagation();
        handleSelect(value);
      }}
      role="option"
      aria-selected={isSelected}
    >
      {isSelected && (
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
          <Check className="h-4 w-4" />
        </span>
      )}
      {children}
    </div>
  );
};

// Re-define Select to include LabelMap state
const SelectRoot = ({ children, value, onValueChange, defaultValue }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value || defaultValue || "");
  const [labelMap, setLabelMap] = useState({});
  const selectRef = useRef(null);

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  const handleSelect = (val) => {
    setSelectedValue(val);
    if (onValueChange) {
      onValueChange(val);
    }
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <SelectContext.Provider value={{ value: selectedValue, handleSelect, isOpen, setIsOpen, labelMap, setLabelMap }}>
      <div className="relative w-full" ref={selectRef}>
        {children}
      </div>
    </SelectContext.Provider>
  );
};

export { SelectRoot as Select, SelectTrigger, SelectValue, SelectContent, SelectItem };
